export default class Posts {
    constructor() {
        this.sentinel = document.querySelector('.posts-sentinel');
        this.grid = document.querySelector('.posts-grid');
        this.postsEnd = document.querySelector('.posts-end');
        this.template = document.getElementById('tmpl-post-card');

        if (!this.sentinel || !this.grid) {
            return;
        }

        this.postType = this.sentinel.dataset.type || this.grid.dataset.postType || 'blog';
        this.currentPage = parseInt(this.sentinel.dataset.paged, 10) || 1;
        this.maxPages = parseInt(this.sentinel.dataset.maxPages, 10) || 1;
        this.hasMore = this.sentinel.dataset.hasMore === 'true';
        this.isLoading = false;
        this.abortController = null;

        this.initStatusAnnouncer();
        this.initObserver();
    }

    initStatusAnnouncer() {
        this.statusAnnouncer = document.getElementById('posts-status');
        if (!this.statusAnnouncer) {
            this.statusAnnouncer = document.createElement('p');
            this.statusAnnouncer.id = 'posts-status';
            this.statusAnnouncer.className = 'sr-only';
            this.statusAnnouncer.setAttribute('role', 'status');
            this.statusAnnouncer.setAttribute('aria-live', 'polite');
            this.statusAnnouncer.setAttribute('aria-atomic', 'true');
            this.sentinel.parentNode?.insertBefore(this.statusAnnouncer, this.sentinel);
        }
    }

    announce(message) {
        if (this.statusAnnouncer) {
            this.statusAnnouncer.textContent = message;
        }
    }

    initObserver() {
        if (!this.hasMore || this.currentPage >= this.maxPages) {
            this.finishLoading();
            return;
        }

        const options = {
            root: null,
            rootMargin: '0px 0px 400px 0px',
            threshold: 0,
        };

        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    this.fetchNextBatch();
                }
            });
        }, options);

        this.observer.observe(this.sentinel);
    }

    async fetchNextBatch() {
        if (this.isLoading || !this.hasMore) {
            return;
        }

        this.isLoading = true;
        this.sentinel.classList.add('is-loading');

        const nextPage = this.currentPage + 1;
        const ajaxUrl = (typeof w4d_frontend !== 'undefined' && w4d_frontend.url)
            ? w4d_frontend.url
            : '/wp-admin/admin-ajax.php';
        const nonce = (typeof w4d_frontend !== 'undefined' && w4d_frontend.nonce)
            ? w4d_frontend.nonce
            : '';

        const params = new URLSearchParams({
            action: 'w4d_load_more_posts',
            _nonce: nonce,
            type: this.postType,
            paged: String(nextPage),
        });

        this.abortController = new AbortController();

        try {
            const response = await fetch(`${ajaxUrl}?${params.toString()}`, {
                method: 'GET',
                signal: this.abortController.signal,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            if (!response.ok) {
                throw new Error(`Server returned HTTP ${response.status}`);
            }

            const json = await response.json();
            const payload = json.data ? json.data : json;
            const posts = Array.isArray(payload.posts) ? payload.posts : [];
            const hasMore = Boolean(payload.has_more);

            if (posts.length > 0) {
                this.currentPage = nextPage;
                this.renderPosts(posts);
                this.announce(`Učitano je još ${posts.length} objava.`);
            }

            if (!hasMore || this.currentPage >= this.maxPages || posts.length === 0) {
                this.finishLoading();
            } else {
                this.sentinel.dataset.paged = String(this.currentPage);
                this.sentinel.dataset.hasMore = 'true';
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Error loading more posts:', error);
            }
        } finally {
            this.isLoading = false;
            this.sentinel.classList.remove('is-loading');
        }
    }

    renderPosts(posts) {
        const fragment = document.createDocumentFragment();

        posts.forEach((post) => {
            const card = this.createPostCard(post);
            if (card) {
                fragment.appendChild(card);
            }
        });

        this.grid.appendChild(fragment);
    }

    createPostCard(post) {
        let card = null;

        if (this.template && this.template.content && this.template.content.firstElementChild) {
            card = this.template.content.firstElementChild.cloneNode(true);
        } else {
            const existingCard = this.grid.querySelector('.post-card');
            if (existingCard) {
                card = existingCard.cloneNode(true);
            }
        }

        if (!card) {
            card = document.createElement('article');
            card.className = 'post-card';
            card.innerHTML = `
                <div class="post-card__media"></div>
                <div class="post-card__body">
                    <div class="post-card__meta">
                        <time datetime=""></time>
                    </div>
                    <h3 class="post-card__title">
                        <a href=""></a>
                    </h3>
                    <p class="post-card__excerpt"></p>
                </div>
            `;
        }

        card.classList.add('post-card--loaded');
        card.classList.toggle('post-card--featured', Boolean(post.is_featured));
        for (const property of ['--featured-h', '--featured-s', '--featured-l']) {
            card.style.removeProperty(property);
            const value = post.is_featured && post.color_properties?.[property];
            if (typeof value === 'string' && /^\d+(\.\d+)?%?$/.test(value)) {
                card.style.setProperty(property, value);
            }
        }

        // 1. Thumbnail / Media
        const mediaContainer = card.querySelector('.post-card__media');
        if (mediaContainer) {
            mediaContainer.replaceChildren();

            if (post.thumbnail && post.thumbnail.src) {
                const link = document.createElement('a');
                link.href = post.permalink || '#';
                link.tabIndex = -1;
                link.setAttribute('aria-hidden', 'true');

                const img = document.createElement('img');
                img.src = post.thumbnail.src;
                if (post.thumbnail.srcset) img.srcset = post.thumbnail.srcset;
                if (post.thumbnail.sizes) img.sizes = post.thumbnail.sizes;
                if (post.thumbnail.width) img.width = post.thumbnail.width;
                if (post.thumbnail.height) img.height = post.thumbnail.height;
                img.alt = post.thumbnail.alt || post.title || '';
                img.loading = 'lazy';
                img.decoding = 'async';
                img.className = 'attachment-medium_large size-medium_large wp-post-image';

                link.appendChild(img);
                mediaContainer.appendChild(link);
            } else {
                const placeholder = document.createElement('div');
                placeholder.className = 'post-media-placeholder';
                placeholder.setAttribute('aria-hidden', 'true');
                placeholder.innerHTML = `
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                        <polyline points="21 15 16 10 5 21"></polyline>
                    </svg>
                `;
                mediaContainer.appendChild(placeholder);
            }
        }

        // 2. Metadata (Date & Badges)
        const metaContainer = card.querySelector('.post-card__meta');
        if (metaContainer) {
            let timeEl = metaContainer.querySelector('time');
            if (!timeEl) {
                timeEl = document.createElement('time');
                metaContainer.prepend(timeEl);
            }

            if (post.date) {
                timeEl.setAttribute('datetime', post.date.datetime || '');
                timeEl.textContent = post.date.formatted || '';
            }

            metaContainer.querySelectorAll('.badge').forEach((badge) => badge.remove());

            if (Array.isArray(post.badges)) {
                post.badges.forEach((badgeText) => {
                    const badge = document.createElement('span');
                    badge.className = post.is_featured ? 'badge badge--danger' : 'badge';
                    badge.textContent = badgeText;
                    metaContainer.appendChild(badge);
                });
            }
        }

        // 3. Title & Link
        const titleLink = card.querySelector('.post-card__title a');
        if (titleLink) {
            titleLink.href = post.permalink || '#';
            titleLink.textContent = post.title || '';
        }

        // 4. Excerpt
        const excerptEl = card.querySelector('.post-card__excerpt');
        if (excerptEl) {
            excerptEl.textContent = post.excerpt || '';
        }

        return card;
    }

    finishLoading() {
        this.hasMore = false;
        if (this.sentinel) {
            this.sentinel.dataset.hasMore = 'false';
            if (this.observer) {
                this.observer.unobserve(this.sentinel);
            }
            this.sentinel.hidden = true;
        }

        if (this.postsEnd) {
            this.postsEnd.hidden = false;
            const messageEl = this.postsEnd.querySelector('.posts-end__message');
            if (messageEl) {
                this.announce(messageEl.textContent.trim());
            }
        }
    }
}

document.addEventListener('DOMContentLoaded', () => {
    new Posts();
});
